import React from 'react'
import Head from 'next/head'
import {
  Button,
  Icons,
  List,
  notification,
  Popconfirm,
  Space,
  Steps,
  Table,
  Tag,
  TextField,
  Tooltip,
  Typography,
  useTable,
} from '@pankod/refine-antd'
import {
  IResourceComponentsProps,
  GetListResponse,
  useCan,
  useInvalidate,
  useNavigation,
  useCreate,
  useUpdate,
} from '@pankod/refine-core'
import { useRouter } from 'next/router'

import {
  CertificateTemplate,
  CreateCertificateTemplatePayload,
  UpdateCertificateTemplateCategoryPayload,
  UpdateSupportingDocumentPayload,
} from './types'
import { TCertificateCategory } from '../certificate-categories'
import {
  injectBsreStampIntoSVG,
  removeBsreStampFromSVG,
  svgHasContent,
  saveTemplateSVG,
} from './fabric/utils/offscreenBsreStamp'
import { NestedBreadcrumb } from '../components/NestedBreadcrumb'
import { kapabilitasMapper } from '@components/DataTableParticipants/blocks/utils'
import { TCommonError, TCommonResponse } from 'src/interfaces/common'
import { showErrorNotification } from '@resources/angkatan-ppg-management/utils'
import { getGuruToken } from 'src/helpers/session'
import CreateTemplateDesignModal from '@resources/lms-management/certificate-template/blocks/CreateTemplateDesignModal'
import EditCertificateCategoryModal from '@resources/lms-management/certificate-template/blocks/EditCertificateCategoryModal'
import UploadSupportingDocumentModal from '@resources/lms-management/certificate-template/blocks/UploadSupportingDocumentModal'
import { WorkflowProgressDrawer } from '@resources/lms-management/certificate-template/blocks/WorkflowProgressDrawer'

export const CertificateTemplateList: React.FC<
  IResourceComponentsProps<GetListResponse<CertificateTemplate>>
> = ({ options }) => {
  const [createModalVisible, setCreateModalVisible] = React.useState(false)
  const [selectedTemplate, setSelectedTemplate] =
    React.useState<CertificateTemplate | null>(null)
  const [selectedTemplateForDoc, setSelectedTemplateForDoc] =
    React.useState<CertificateTemplate | null>(null)
  const [selectedWorkflowSubmissionId, setSelectedWorkflowSubmissionId] =
    React.useState<string | null>(null)
  const [loadingDocId, setLoadingDocId] = React.useState<string | null>(null)
  const { query, push } = useRouter()
  const { listUrl, list, showUrl } = useNavigation()
  const invalidate = useInvalidate()
  const { data: canAccessProgram } = useCan({
    action: 'show',
    resource: 'lms-program-management',
  })
  const programId = query.programId as string

  const handleViewDocument = async (record: CertificateTemplate) => {
    setLoadingDocId(record.id)
    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_LMS_API_URL}/programs/${programId}/certificate-templates/${record.id}`,
        {
          headers: { Authorization: `Bearer ${getGuruToken()}` },
        },
      )

      if (!response.ok) {
        throw new Error('Gagal memuat detail template sertifikat')
      }

      const json = await response.json()
      const url = json.data?.supportingDocumentUrl

      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer')
      } else {
        throw new Error('Dokumen tidak ditemukan')
      }
    } catch (e) {
      notification.error({
        message: 'Gagal membuka dokumen',
        description:
          e instanceof Error
            ? e.message
            : 'Terjadi kesalahan yang tidak diketahui',
      })
    } finally {
      setLoadingDocId(null)
    }
  }

  const { tableProps, tableQueryResult } = useTable({
    resource: `programs/${programId}/certificate-templates`,
    dataProviderName: 'lms',
  })

  const { mutateAsync: generateNewTemplate } = useCreate<
    TCommonResponse<CertificateTemplate>,
    TCommonError,
    CreateCertificateTemplatePayload
  >()
  const { mutateAsync: updateTemplateCategory } = useUpdate<
    TCommonResponse<CertificateTemplate>,
    TCommonError,
    UpdateCertificateTemplateCategoryPayload
  >()
  const { mutateAsync: updateSupportingDocument } = useUpdate<
    TCommonResponse<CertificateTemplate>,
    TCommonError,
    UpdateSupportingDocumentPayload
  >()

  if (!programId) {
    list('lms-program-management')
    return null
  }

  const handleCreateTemplate = async (
    value: CreateCertificateTemplatePayload,
    file?: File,
  ) => {
    const { data } = await generateNewTemplate({
      resource: `programs/${programId}/certificate-templates`,
      values: { ...value },
      dataProviderName: 'lms',
      successNotification: false,
      errorNotification: (error) =>
        showErrorNotification(error, 'Terjadi kesalahan saat membuat template'),
    })
    if (file) {
      const formData = new FormData()
      formData.append('file', file)
      await fetch(
        `${process.env.NEXT_PUBLIC_LMS_API_URL}/programs/${programId}/certificate-templates/${data.data.id}/supporting-document`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${getGuruToken()}` },
          body: formData,
        },
      )
    }
    const redirectUrl =
      showUrl('certificate-templates', data.data.id) + `?programId=${programId}`
    push(redirectUrl)
  }

  const syncBsreStamp = async (
    action: 'inject' | 'remove',
    template: CertificateTemplate,
  ) => {
    const actionFn =
      action === 'inject' ? injectBsreStampIntoSVG : removeBsreStampFromSVG

    const processPage = async (url: string, page: 'front' | 'back') => {
      if (svgHasContent(url)) {
        const result = await actionFn(url)
        await saveTemplateSVG(
          programId as string,
          template.id,
          page,
          result.svgBlob,
          result.usedSystemVariables,
          template.kapabilitas,
        )
      }
    }

    await processPage(template.frontTemplateUrl, 'front')
    await processPage(template.backTemplateUrl, 'back')
  }

  const handleUpdateCategory = async (
    values: UpdateCertificateTemplateCategoryPayload,
    selectedCategory: TCertificateCategory | null,
  ) => {
    if (!selectedTemplate) {
      return
    }

    const newHasSigners = (selectedCategory?.actors || []).some(
      (actor) => actor.role === 'signer',
    )
    const currentHasSigners = (
      selectedTemplate.certificateCategory?.actors || []
    ).some((actor) => actor.role === 'signer')

    const previousCategoryId = selectedTemplate.certificateCategory?.id ?? null

    await updateTemplateCategory(
      {
        resource: `programs/${programId}/certificate-templates`,
        id: selectedTemplate.id,
        values,
        metaData: {
          httpMethod: 'patch',
        },
        dataProviderName: 'lms',
        successNotification: {
          message: 'Kategori sertifikat berhasil diperbarui',
          type: 'success',
          description: 'Sukses',
        },
        errorNotification: (error) =>
          showErrorNotification(
            error,
            'Terjadi kesalahan saat memperbarui kategori sertifikat',
          ),
      },
      {
        onSuccess: async () => {
          let hasError = false
          if (newHasSigners && !currentHasSigners) {
            try {
              await syncBsreStamp('inject', selectedTemplate)
            } catch (e: any) {
              hasError = true
              showErrorNotification(
                e,
                'Terjadi kesalahan saat menambahkan stempel BSrE ke dalam template.',
              )
            }
          } else if (!newHasSigners && currentHasSigners) {
            try {
              await syncBsreStamp('remove', selectedTemplate)
            } catch (e: any) {
              hasError = true
              showErrorNotification(
                e,
                'Terjadi kesalahan saat menghapus stempel BSrE dari template.',
              )
            }
          }

          if (hasError) {
            try {
              await updateTemplateCategory({
                resource: `programs/${programId}/certificate-templates`,
                id: selectedTemplate.id,
                values: {
                  certificateCategoryId:
                    Number(previousCategoryId) === 0
                      ? null
                      : Number(previousCategoryId),
                },
                metaData: { httpMethod: 'patch' },
                dataProviderName: 'lms',
              })
            } catch (e) {
              showErrorNotification(
                e as Error,
                'Gagal mengembalikan kategori sertifikat. Harap periksa template Anda secara manual.',
              )
            }
          }

          setSelectedTemplate(null)
          invalidate({
            invalidates: ['all'],
            dataProviderName: 'lms',
            resource: `programs/${programId}/certificate-templates`,
          })
        },
      },
    )
  }

  const handleDeleteSupportingDocument = async (
    record: CertificateTemplate,
  ) => {
    await updateSupportingDocument(
      {
        resource: `programs/${programId}/certificate-templates`,
        id: record.id,
        values: { supportingDocumentUrl: '' },
        metaData: { httpMethod: 'patch' },
        dataProviderName: 'lms',
        successNotification: {
          message: 'Dokumen pendukung berhasil dihapus',
          type: 'success',
          description: 'Sukses',
        },
        errorNotification: (error) =>
          showErrorNotification(
            error,
            'Terjadi kesalahan saat menghapus dokumen pendukung',
          ),
      },
      {
        onSuccess: () => {
          invalidate({
            invalidates: ['list'],
            dataProviderName: 'lms',
            resource: `programs/${programId}/certificate-templates`,
          })
        },
      },
    )
  }

  return (
    <>
      <List
        title="Daftar Template Sertifikat"
        headerButtons={() => {
          // renderCreateTemplateButton()
          return (
            <Button
              type="primary"
              icon={<Icons.PlusOutlined />}
              onClick={() => setCreateModalVisible(true)}
            >
              Buat Template Baru
            </Button>
          )
        }}
        breadcrumb={
          <NestedBreadcrumb
            injectedItems={[
              ...(canAccessProgram?.can
                ? [
                    {
                      label: 'Program',
                      href: listUrl('lms-program-management'),
                    },
                  ]
                : []),
              {
                label: programId,
                href: canAccessProgram?.can
                  ? showUrl('lms-program-management', programId)
                  : listUrl('lms-program-local-management'),
              },
              {
                label: 'Template Sertifikat',
              },
            ]}
          />
        }
      >
        <Head>
          <title>LMS Admin | {options?.label}</title>
        </Head>

        <Table {...tableProps} rowKey="id">
          <Table.Column
            dataIndex="id"
            title="ID"
            render={(value) => (
              <TextField className="!whitespace-nowrap" value={value} />
            )}
          />
          <Table.Column
            dataIndex="kapabilitas"
            title="Kapabilitas"
            render={(value) => <TextField value={kapabilitasMapper(value)} />}
          />
          <Table.Column
            dataIndex="certificateType"
            title="Jenis Sertifikat"
            render={(value) => (
              <TextField
                value={
                  value === 'COMPLETION'
                    ? 'Penyelesaian (Lulus)'
                    : 'Partisipasi (Tidak Lulus)'
                }
              />
            )}
          />
          <Table.Column
            dataIndex="state"
            title="Status"
            render={(value) =>
              value === 'TEMPLATE_PUBLISHED' ? (
                <Tag color="success">Published</Tag>
              ) : (
                <Tag color="warning">Draft</Tag>
              )
            }
          />
          <Table.Column<CertificateTemplate>
            dataIndex="hasSupportingDocument"
            title="Dokumen"
            render={(value, record) =>
              value ? (
                <Button
                  type="default"
                  icon={<Icons.FileTextOutlined />}
                  className="p-0"
                  onClick={() => handleViewDocument(record)}
                  loading={loadingDocId === record.id}
                  size="small"
                >
                  Lihat Dokumen
                </Button>
              ) : (
                <Typography.Text italic type="secondary">
                  Kosong
                </Typography.Text>
              )
            }
          />
          <Table.Column<CertificateTemplate>
            dataIndex="certificateCategory"
            title="Kategori"
            render={(_, record) => {
              const category = record.certificateCategory
              if (!category) {
                return (
                  <Typography.Text italic type="secondary">
                    &mdash;
                  </Typography.Text>
                )
              }
              const actors = [...(category.actors || [])].sort(
                (a, b) => a.order - b.order,
              )
              return (
                <Space>
                  <Tooltip
                    title={
                      actors.length > 0 ? (
                        <Steps direction="vertical" size="small" current={-1}>
                          {actors.map((actor) => (
                            <Steps.Step
                              key={`${actor.userId}-${actor.order}`}
                              title={
                                <span className="text-white">
                                  {actor.name || actor.email || actor.userId}
                                </span>
                              }
                              description={
                                <span className="text-xs text-gray-300">
                                  {actor.jabatan || '-'} &middot;{' '}
                                  {actor.role === 'approver'
                                    ? 'Approver'
                                    : 'Signer'}
                                </span>
                              }
                            />
                          ))}
                        </Steps>
                      ) : undefined
                    }
                  >
                    <Icons.InfoCircleOutlined
                      className="cursor-help"
                      style={{ color: '#888' }}
                    />
                  </Tooltip>
                  <Tag>{category.name} </Tag>
                </Space>
              )
            }}
          />
          <Table.Column<CertificateTemplate>
            title="Actions"
            fixed="right"
            render={(_, record) => (
              <Space direction="vertical" className="w-full max-w-[160px]">
                <Button
                  icon={<Icons.EditOutlined />}
                  onClick={() => {
                    push(
                      showUrl('certificate-templates', record.id) +
                        `?programId=${programId}`,
                    )
                  }}
                  size="small"
                  block
                >
                  Edit Template
                </Button>
                <Button
                  icon={<Icons.FormOutlined />}
                  disabled={record.state !== 'TEMPLATE_DRAFTED'}
                  onClick={() => setSelectedTemplate(record)}
                  size="small"
                  block
                >
                  Ubah Kategori
                </Button>
                {record.certificateTteSubmissionId && (
                  <Button
                    icon={<Icons.AuditOutlined />}
                    onClick={() =>
                      setSelectedWorkflowSubmissionId(
                        record.certificateTteSubmissionId ?? null,
                      )
                    }
                    size="small"
                    block
                  >
                    Progres TTE
                  </Button>
                )}
                {record.hasSupportingDocument ? (
                  <Popconfirm
                    title="Hapus dokumen pendukung?"
                    okText="Hapus"
                    cancelText="Batal"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => handleDeleteSupportingDocument(record)}
                    disabled={record.state !== 'TEMPLATE_DRAFTED'}
                  >
                    <Button
                      icon={<Icons.DeleteOutlined />}
                      size="small"
                      danger
                      block
                      disabled={record.state !== 'TEMPLATE_DRAFTED'}
                    >
                      Hapus Dokumen
                    </Button>
                  </Popconfirm>
                ) : (
                  <Button
                    icon={<Icons.UploadOutlined />}
                    size="small"
                    block
                    disabled={record.state !== 'TEMPLATE_DRAFTED'}
                    onClick={() => setSelectedTemplateForDoc(record)}
                  >
                    Tambah Dokumen
                  </Button>
                )}
              </Space>
            )}
          />
        </Table>
        <CreateTemplateDesignModal
          visible={createModalVisible}
          onClose={() => setCreateModalVisible(false)}
          onConfirm={handleCreateTemplate}
          unavailableTypes={tableQueryResult?.data?.data.map(
            (item) =>
              ({
                kapabilitas: item.kapabilitas,
                certificateType: item.certificateType,
              }) as { kapabilitas: string; certificateType: string },
          )}
        />
        <EditCertificateCategoryModal
          visible={!!selectedTemplate}
          initialCertificateCategoryId={
            selectedTemplate?.certificateCategory?.id
          }
          onClose={() => setSelectedTemplate(null)}
          onConfirm={handleUpdateCategory}
        />
        {selectedTemplateForDoc && (
          <UploadSupportingDocumentModal
            visible={!!selectedTemplateForDoc}
            onClose={() => setSelectedTemplateForDoc(null)}
            programId={programId}
            certificateId={selectedTemplateForDoc.id}
          />
        )}
        <WorkflowProgressDrawer
          open={!!selectedWorkflowSubmissionId}
          submissionId={selectedWorkflowSubmissionId}
          onClose={() => setSelectedWorkflowSubmissionId(null)}
        />
      </List>
    </>
  )
}
